import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CaretLeft, CaretRight, Lightning, Users } from "@phosphor-icons/react";
import { fileUrl } from "../../lib/api";
import { useLang } from "../../i18n";

const HERO_IMG = "https://images.pexels.com/photos/5051381/pexels-photo-5051381.jpeg?auto=compress&cs=tinysrgb&w=1920";

function MaskedLine({ children, delay }) {
    return (
        <span className="block overflow-hidden">
            <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
            >
                {children}
            </motion.span>
        </span>
    );
}

export default function Hero({ settings, medals = 0 }) {
    const { t } = useLang();
    const slides = useMemo(() => {
        const fromSettings = Array.isArray(settings.hero_image_urls) ? settings.hero_image_urls.filter(Boolean) : [];
        const legacy = settings.hero_image_url ? [settings.hero_image_url] : [];
        const merged = [...new Set([...fromSettings, ...legacy])].slice(0, 10);
        return merged.length ? merged : [HERO_IMG];
    }, [settings.hero_image_urls, settings.hero_image_url]);
    const [active, setActive] = useState(0);

    useEffect(() => {
        if (slides.length <= 1) return undefined;
        const timer = setInterval(() => setActive((current) => (current + 1) % slides.length), 5000);
        return () => clearInterval(timer);
    }, [slides.length]);

    const go = (direction) => {
        setActive((current) => (current + direction + slides.length) % slides.length);
    };

    return (
        <section className="relative min-h-screen overflow-hidden bg-[#D21E06]" data-testid="hero-section">
            <div className="absolute inset-0" data-testid="hero-carousel">
                {slides.map((slide, index) => (
                    <motion.img
                        key={slide}
                        src={fileUrl(slide)}
                        alt={`Foto kegiatan Mandalika Inlineskate ${index + 1}`}
                        className="absolute inset-0 h-full w-full object-cover"
                        initial={false}
                        animate={{
                            opacity: index === active ? 1 : 0,
                            scale: index === active ? 1 : 1.035,
                            filter: index === active ? "blur(0px)" : "blur(14px)",
                        }}
                        transition={{ duration: 1.35, ease: [0.22, 1, 0.36, 1] }}
                        data-testid={index === active ? "hero-carousel-image" : `hero-carousel-image-inactive-${index}`}
                    />
                ))}
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#D21E06]/20 via-transparent to-white/5" />
            </div>
            <div className="hidden lg:flex absolute right-10 top-1/2 -translate-y-1/2 z-10">
                <motion.span
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.4, duration: 1 }}
                    className="font-display uppercase text-[#D21E06]/15 text-8xl tracking-widest"
                    style={{ writingMode: "vertical-rl" }}
                >
                    Speed · Lombok
                </motion.span>
            </div>

            <div className="relative z-10 min-h-screen flex flex-col">
                <div className="bg-white/10 flex-1 flex items-center">
                    <div className="max-w-7xl mx-auto px-6 md:px-10 w-full pt-32 md:pt-36 lg:pt-40 pb-10">
                <div className="max-w-3xl">
                    <motion.div
                        initial={{ opacity: 0, x: -24 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3, duration: 0.8 }}
                        className="flex items-center gap-3 mb-4 md:mb-6"
                    >
                        <Lightning size={18} weight="fill" className="text-[#D21E06]" />
                        <span className="text-xs uppercase tracking-[0.3em] text-neutral-700 [text-shadow:0_1px_10px_rgba(255,255,255,0.9)] [-webkit-text-stroke:0.4px_rgba(255,255,255,0.9)]" data-testid="hero-kicker">
                            {t.hero_kicker}
                        </span>
                    </motion.div>

                    <h1 className="font-display uppercase leading-[0.9] tracking-tight text-neutral-900 [-webkit-text-stroke:2.5px_rgba(255,255,255,0.85)] [paint-order:stroke_fill] text-[15vw] sm:text-7xl md:text-8xl lg:text-[7.5rem]" data-testid="hero-title">
                        <MaskedLine delay={0.45}>Mandalika</MaskedLine>
                        <MaskedLine delay={0.6}>
                            <span className="text-[#D21E06]">Inline</span>skate
                        </MaskedLine>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1, duration: 0.8 }}
                        className="mt-4 md:mt-6 inline-block max-w-xl bg-[#D21E06] text-white text-sm md:text-base font-light tracking-wide leading-relaxed px-5 py-3"
                        data-testid="hero-subtitle"
                    >
                        {t.hero_subtitle}
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.15, duration: 0.8 }}
                        className="mt-6 md:mt-10 flex flex-wrap gap-3 md:gap-4"
                    >
                        <Link
                            to="/atlet"
                            data-testid="hero-athletes-button"
                            className="group bg-[#D21E06] hover:bg-neutral-900 text-white text-xs uppercase tracking-[0.25em] px-8 py-4 transition-colors duration-300 flex items-center gap-3"
                        >
                            {t.hero_cta_athletes}
                            <Users size={16} weight="bold" className="transition-transform duration-300 group-hover:scale-110" />
                        </Link>
                        <Link
                            to="/daftar-siswa-baru"
                            data-testid="hero-new-student-button"
                            className="border border-neutral-900/35 bg-white/70 hover:bg-white hover:text-[#D21E06] text-neutral-900 text-xs uppercase tracking-[0.25em] px-8 py-4 transition-colors duration-300"
                        >
                            {t.nav_new_student}
                        </Link>
                    </motion.div>
                </div>
                    </div>
                </div>

                <div className="bg-[#D21E06]/95">
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.3, duration: 0.8 }}
                    className="max-w-7xl mx-auto px-6 md:px-10 py-6 md:py-8 flex flex-wrap items-end justify-between gap-8"
                >
                    <div className="flex gap-8 md:gap-12 border-t border-white/30 pt-5 md:pt-8" data-testid="hero-stats">
                        <div>
                            <p className="font-display text-3xl md:text-5xl text-white leading-none" data-testid="hero-stat-medals">{medals}</p>
                            <p className="text-[10px] uppercase tracking-[0.25em] text-white/65 mt-2">{t.hero_stat_medals}</p>
                        </div>
                        {settings.founded_year ? (
                            <div>
                                <p className="font-display text-3xl md:text-5xl text-racing-gold leading-none" data-testid="hero-stat-founded">{settings.founded_year}</p>
                                <p className="text-[10px] uppercase tracking-[0.25em] text-white/65 mt-2">{t.hero_stat_founded}</p>
                            </div>
                        ) : null}
                    </div>

                    {slides.length > 1 && (
                        <div className="flex flex-col items-end gap-3">
                            <div className="flex gap-2" data-testid="hero-carousel-dots">
                                {slides.map((slide, index) => (
                                    <button
                                        key={slide}
                                        type="button"
                                        onClick={() => setActive(index)}
                                        aria-label={`Foto hero ${index + 1}`}
                                        data-testid={`hero-dot-${index}`}
                                        className={`h-2.5 transition-all duration-300 ${active === index ? "w-8 bg-racing-gold" : "w-2.5 bg-white/40 hover:bg-white/70"}`}
                                    />
                                ))}
                            </div>
                            <div className="flex gap-2">
                                <button type="button" onClick={() => go(-1)} data-testid="hero-prev" aria-label="Foto sebelumnya" className="p-2 border border-white/40 text-white/85 hover:text-white hover:border-white transition-colors">
                                    <CaretLeft size={18} />
                                </button>
                                <button type="button" onClick={() => go(1)} data-testid="hero-next" aria-label="Foto berikutnya" className="p-2 border border-white/40 text-white/85 hover:text-white hover:border-white transition-colors">
                                    <CaretRight size={18} />
                                </button>
                            </div>
                        </div>
                    )}
                </motion.div>
                </div>
            </div>
        </section>
    );
}
